import { Company } from '@apis/Customers/model';
import { postDailyRollupQuery, postDailyRollupQueryMonths } from '@apis/Invoices';
import { BillingInvoice, Query, SchemaField } from '@apis/Invoices/model';
import { Box, Button, Group, Modal, Space, Sx, Text, ThemeIcon, Tooltip, useMantineTheme } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { FillerSwitch } from '@root/Design/Filler';
import { ICompanyContextToken } from '@root/Services/Customers/CompanyContext';
import { useDiMemo } from '@root/Services/DI';
import { EventEmitter, useEventValue, useToggle } from '@root/Services/EventEmitter';
import { FormatService } from '@root/Services/FormatService';
import { IntegrationSchemaService } from '@root/Services/Integrations/IntegrationSchemaService';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { AdjPivotName, InvoiceSchemaService } from '@root/Services/Invoices/InvoiceSchemaService';
import { ComponentProps, CSSProperties, useCallback, useMemo, useState } from 'react';
import { Compass, Upload } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { DataGrid } from '../DataGrid';
import { ColumnConfig, DataGridProps } from '../DataGrid/Models';
import { DailyInvoiceGrid, DailyInvoiceRow } from '../Invoices/DailyInvoiceGrid';
import { LineItemFileUploader } from './LineItemFileUploader';

@injectable()
class CustomLineItemModel {
    public invoice!: BillingInvoice;
    private _columns: ColumnConfig<Record<string, unknown>>[] = [];
    private _fields: SchemaField[] = [];
    private static readonly _unimportableFields = new Set([
        'AdjustedCashCost',
        'AdjustedAmortizedCost',
        'AdjustmentLog',
        'BillingPeriodEnd',
        'BillingPeriodStart',
        'ChargePeriodEnd',
        'ChargePeriodStart',
        'CompanyId',
        'ExternalCostAdjustment',
        'RecordCount',
        'TagChangeLog',
        'UniqueId',
        'UsageMonth',
    ]);

    public readonly loading = new EventEmitter<boolean>(true);
    public get columns() {
        return this._columns;
    }
    public get fields() {
        return this._fields;
    }
    public requestDateRange!: { from: Date; to: Date };

    public constructor(
        @inject(InvoiceApiService) public readonly invoiceApi: InvoiceApiService,
        @inject(IntegrationSchemaService) private readonly integrationSchemaSvc: IntegrationSchemaService,
        @inject(ICompanyContextToken) public readonly company: Company,
        @inject(FormatService) private readonly fmtSvc: FormatService
    ) {}

    public init(invoice: BillingInvoice) {
        this.invoice = invoice;

        const date = new Date(this.invoice.Year!, this.invoice.Month! - 1, 1);
        this.requestDateRange = { from: date, to: date };
        this.loadSchema();

        return this;
    }

    public serializeLineItem(item: Record<string, unknown>) {
        const values: unknown[] = [];
        for (const field of this.fields) {
            let value = item[field.Field ?? ''] ?? '';
            values.push(value);
        }
        return values.join('\t');
    }

    public async loadSchema() {
        this.loading.emit(true);
        try {
            const month = new Date(this.invoice.Year!, this.invoice.Month! - 1, 1);
            const invoiceSchemaReq = postDailyRollupQueryMonths({ IncludeSchema: true, Take: 0 }, { months: [this.fmtSvc.formatYearMonth(month)] });
            const integrationReq = this.integrationSchemaSvc.getIntegrationSchema();
            const [invoiceSchemas, integrationSchemas] = await Promise.all([invoiceSchemaReq, integrationReq]);

            const integrationNames = [...new Set(integrationSchemas.flatMap((s) => s.Name ?? ''))];
            const rootType = invoiceSchemas.Types?.find((t) => t.IsRoot);
            const rawFields = rootType?.Fields || [];

            const fields = this.updateFields(rawFields, integrationNames);
            this._columns = this.createColumnConfig(fields);
            this._fields = fields;
        } finally {
            this.loading.emit(false);
        }
    }

    public createColumnConfig(fields: SchemaField[]): ColumnConfig<Record<string, unknown>>[] {
        return fields.map((f, index) => ({
            id: `col_${index}`,
            header: f.Field!,
            accessor: f.Field!,
            defaultWidth: 150,
            type: 'string',
            noRemove: true,
            noReorder: true,
        }));
    }

    public parseData(serializedData: string) {
        try {
            return JSON.parse(serializedData) as Record<string, unknown>[];
        } catch {
            return [];
        }
    }

    public cleanNumericData(data: Record<string, unknown>[]) {
        const numericFieldNames = new Set(this._fields.filter((f) => f.Field && f.TypeName === 'number').map((f) => f.Field!));

        if (numericFieldNames.size === 0) {
            return data;
        }

        for (const item of data) {
            for (const fieldName of numericFieldNames) {
                if (fieldName in item) {
                    const value = item[fieldName];
                    if (typeof value === 'string') {
                        const cleaned = value.replace(/[,$\s]/g, '');
                        if (cleaned === '') {
                            continue;
                        }
                        const parsed = Number(cleaned);
                        if (!isNaN(parsed)) {
                            item[fieldName] = parsed;
                        }
                    }
                }
            }
        }

        return data;
    }

    public cleanData(data: Record<string, unknown>[]) {
        this.cleanNumericData(data);
        for (const item of data) {
            if ('BilledDate' in item) {
                let value = item['BilledDate'];
                if (value instanceof Date) {
                    item['BilledDate'] = value.getDay() + 1;
                }
            }
        }
        return JSON.stringify(data);
    }

    private updateFields(fields: SchemaField[], integrationNames: string[]) {
        const unimportableFields = CustomLineItemModel._unimportableFields;
        const filteredFields = fields.filter((f) => {
            return (
                f.Field &&
                !unimportableFields.has(f.Field) &&
                !integrationNames.some((n) => f.Field!.startsWith(n)) &&
                !f.Field.startsWith('resourceTags') &&
                !AdjPivotName.isAdjField(f.Field!)
            );
        });
        const sortedFields = filteredFields.sort((a, b) => a.Field!.localeCompare(b.Field!, undefined, { sensitivity: 'base' }));

        return sortedFields;
    }
}

interface LineItemImporterProps {
    value: string | null;
    onChange: (value: string | null) => void;
    invoice: BillingInvoice;
}
export function LineItemImporter(props: LineItemImporterProps) {
    const { value, onChange, invoice } = props;
    const model = useDiMemo(CustomLineItemModel, [invoice], (m) => m.init(invoice));
    const loading = useEventValue(model.loading);
    const handleImport = useCallback((data: Record<string, unknown>[]) => onChange(model.cleanData(data)), [onChange]);

    const gridProps = useMemo((): DataGridProps<Record<string, unknown>> => {
        if (!loading) {
            return {
                columns: model.columns,
                dataSource: model.parseData(value || '[]'),
                exportName: `${model.company.CompanyName} Imported Line Items ${invoice.Year}-${invoice.Month}`,
                hideColumnSelector: true,
            };
        } else {
            return {
                columns: [],
                dataSource: [],
            };
        }
    }, [value, loading, model]);

    return (
        <FillerSwitch loading={loading}>
            {() => (
                <Box sx={{ height: 400 }}>
                    <DataGrid
                        {...gridProps}
                        rightToolPlaceHolder={
                            <Group pb={5} spacing={10}>
                                <LineItemSampleExplorerButton model={model} />
                                <UploadLineItemsButton onChange={handleImport} />
                            </Group>
                        }
                    />
                </Box>
            )}
        </FillerSwitch>
    );
}

function UploadLineItemsButton(props: { onChange: (value: Record<string, unknown>[]) => void }) {
    const [opened, opener] = useToggle(false);
    return (
        <>
            <Tooltip label="Upload line items from CSV/Excel">
                <ThemeIcon radius={15} size={30} sx={{ cursor: 'pointer' }} variant="outline" onClick={opener.open}>
                    <Upload size={16} />
                </ThemeIcon>
            </Tooltip>
            <LineItemFileUploader onClose={opener.close} onImport={props.onChange} opened={opened} />
        </>
    );
}

function LineItemSampleExplorerButton(props: { model: CustomLineItemModel }) {
    const [opened, opener] = useToggle(false);
    return (
        <>
            <Tooltip label="Find and copy sample line items">
                <ThemeIcon radius={15} size={30} sx={{ cursor: 'pointer' }} variant="outline" onClick={opener.open}>
                    <Compass size={16} />
                </ThemeIcon>
            </Tooltip>
            <Modal
                radius="lg"
                title={
                    <>
                        <Text size="lg">Line Items</Text>
                        <Text size="sm">Find invoice line items, and click to copy, then paste into Excel</Text>
                    </>
                }
                size="calc(100vw - 200px)"
                opened={opened}
                onClose={opener.close}
            >
                <LineItemSampleExplorer model={props.model} />
            </Modal>
        </>
    );
}

function LineItemSampleExplorer(props: { model: CustomLineItemModel }) {
    const { model } = props;
    const { colors } = useMantineTheme();
    const { copy, CopiedState } = useCopy();
    const copyLineItem = useCallback((item: Record<string, unknown>) => {
        if (item) {
            copy(item ? model.serializeLineItem(item) : '');
        }
    }, []);
    const onGridPropsReady = useCallback(
        (gridProps: DataGridProps<DailyInvoiceRow>) => {
            return {
                ...gridProps,
                rowStyle: (item, index, hovered) => ({
                    background: !hovered ? 'transparent' : 'value' in item ? colors.primary[2] : colors.success[2],
                }),
                onRowClick: (item) => {
                    if (!('value' in item)) {
                        copyLineItem(item as unknown as Record<string, unknown>);
                    } else if (typeof gridProps.onRowClick !== 'string') {
                        gridProps.onRowClick?.(item);
                    }
                },
            } as DataGridProps<DailyInvoiceRow>;
        },
        [model]
    );
    const dailyInvoiceGridProps = useMemo(
        () =>
            ({
                onGridPropsReady,
                invoiceApi: model.invoiceApi,
                dateRange: model.requestDateRange,
                persistenceKey: `BillingLineItemExplorer.${model.invoice.CompanyId}`,
                defaultGroupBy: [{ id: 'FOCUS.ServiceName', sortDir: 'Asc', sortMode: 'value' }],
                maxLineItems: 200,
                rightPlaceholder: <CopiedState />,
            } as ComponentProps<typeof DailyInvoiceGrid>),
        []
    );

    return (
        <Box sx={{ height: 'calc(100vh - 200px)' }}>
            <DailyInvoiceGrid {...dailyInvoiceGridProps} />
        </Box>
    );
}

function useCopy() {
    const copyEvt = useMemo(() => new EventEmitter(false), []);
    const CopiedState = useMemo(() => {
        return function CopiedState() {
            const { colors } = useMantineTheme();
            const recentlyCopied = useEventValue(copyEvt);
            const textCss: Sx = {
                background: recentlyCopied ? colors.success[1] : 'transparent',
                transition: 'background 0.2s ease',
                borderRadius: 15,
                lineHeight: '30px',
                padding: '0 15px',
            };
            return (
                <Text color={recentlyCopied ? 'success' : undefined} sx={textCss}>
                    {recentlyCopied ? 'Copied' : 'Click a line item to copy'}
                </Text>
            );
        };
    }, []);
    const copy = useCallback((text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            copyEvt.emit(true);
            setTimeout(() => copyEvt.emit(false), 300);
        });
    }, []);

    return { copy, CopiedState };
}
